import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api, unwrapJson } from '../../../api/client';
import { money, moneyCompact } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { plural } from '../lib';
import { Panel } from './DashboardPage';
import {
  addDays,
  axisDate,
  HEATMAP_WEEKS,
  paymentMix,
  salesHeatmap,
  WEEKDAY_NAMES,
  WEEKDAYS,
  type DateRange,
} from './lib';

type Range = { current: DateRange; interval: 'day' | 'week'; rangeLabel: string };

const axisText = { fontSize: 11 };
/** Recessive grid and axis ink shared by every chart here. */
const chartSx = {
  '& .MuiChartsGrid-line': { strokeDasharray: '4 4', opacity: 0.5 },
  '& .MuiChartsAxis-tickLabel': { fill: 'var(--mui-palette-text-secondary)' },
  '& .MuiChartsLegend-label': { color: 'var(--mui-palette-text-secondary)' },
} as const;

function Empty({ height = 260, children }: { height?: number; children: ReactNode }) {
  return (
    <Box
      sx={{
        height,
        display: 'grid',
        placeItems: 'center',
        borderRadius: '12px',
        border: '1px dashed',
        borderColor: 'divider',
        px: 3,
        textAlign: 'center',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
}

function useMoneyAxis() {
  const { format } = useShop();
  return {
    width: 52,
    valueFormatter: (v: number) => moneyCompact(v, format),
    tickLabelStyle: axisText,
    disableLine: true,
    disableTicks: true,
  };
}

/** Invoiced sales against supplier bills, per period: grouped bars on one money axis. */
export function SalesVsPurchases({ current, interval, rangeLabel }: Range) {
  const { format } = useShop();
  const yAxis = useMoneyAxis();
  const sales = useQuery({
    queryKey: ['reports', 'sales-by-period', current, interval],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/sales/by-period', { params: { query: { ...current, interval } } }),
      ),
  });
  const buys = useQuery({
    queryKey: ['reports', 'purchases-by-period', current, interval],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/purchases/by-period', {
          params: { query: { ...current, interval, source: 'bills' } },
        }),
      ),
  });
  const rows = sales.data?.data ?? [];
  const bought = new Map(
    (buys.data?.data ?? []).map((r) => [r.periodStart.slice(0, 10), Number(r.total)]),
  );
  const any = rows.some((r) => Number(r.total) !== 0) || bought.size > 0;
  const net = Number(sales.data?.totals.total ?? 0) - Number(buys.data?.totals.total ?? 0);
  return (
    <Panel
      title="Sales vs purchases"
      subtitle={
        sales.data && buys.data
          ? `${net >= 0 ? 'Sold' : 'Bought'} ${moneyCompact(Math.abs(net), format)} more than you ${net >= 0 ? 'bought' : 'sold'} · last ${rangeLabel}`
          : `${interval === 'day' ? 'Daily' : 'Weekly'} totals · last ${rangeLabel}`
      }
    >
      {sales.isLoading || buys.isLoading ? (
        <Skeleton variant="rounded" height={260} />
      ) : !any ? (
        <Empty>Sales and supplier bills will appear here side by side.</Empty>
      ) : (
        <Box sx={{ mx: -1 }} data-testid="sales-vs-purchases">
          <BarChart
            height={260}
            margin={{ left: 0, right: 12, top: 8, bottom: 0 }}
            grid={{ horizontal: true }}
            borderRadius={4}
            slotProps={{ legend: { position: { vertical: 'top', horizontal: 'end' } } }}
            xAxis={[
              {
                scaleType: 'band',
                data: rows.map((r) => r.periodStart.slice(0, 10)),
                valueFormatter: (v: string) => axisDate(v, format.locale),
                tickInterval: (_v: string, i: number) =>
                  rows.length <= 10 || (rows.length - 1 - i) % 5 === 0,
                categoryGapRatio: 0.3,
                barGapRatio: 0.1,
                tickLabelStyle: axisText,
                disableLine: true,
                disableTicks: true,
              },
            ]}
            yAxis={[yAxis]}
            series={[
              {
                id: 'sales',
                label: 'Sales',
                data: rows.map((r) => Number(r.total)),
                color: 'var(--viz-1)',
                valueFormatter: (v: number | null) => money(v ?? 0, format),
              },
              {
                id: 'purchases',
                label: 'Purchases',
                data: rows.map((r) => bought.get(r.periodStart.slice(0, 10)) ?? 0),
                color: 'var(--viz-2)',
                valueFormatter: (v: number | null) => money(v ?? 0, format),
              },
            ]}
            sx={chartSx}
          />
        </Box>
      )}
    </Panel>
  );
}

/** Money received from customers by payment method: a donut with the total in the middle. */
export function PaymentMethods({ current, rangeLabel }: Range) {
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['reports', 'payments', current],
    queryFn: () => unwrapJson(api.GET('/reports/payments', { params: { query: current } })),
  });
  const slices = paymentMix(q.data?.data ?? []);
  const total = slices.reduce((n, s) => n + s.value, 0);
  const pct = (v: number) => `${String(Math.round((v / total) * 100))}%`;
  return (
    <Panel title="How customers pay" subtitle={`Money received by method · last ${rangeLabel}`}>
      {q.isLoading ? (
        <Skeleton variant="rounded" height={220} />
      ) : total === 0 ? (
        <Empty height={220}>Customer payments will be split by method here.</Empty>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row', md: 'column', xl: 'row' }}
          spacing={2}
          sx={{ alignItems: 'center' }}
          data-testid="payment-methods"
        >
          <Box sx={{ position: 'relative', width: 184, height: 184, flexShrink: 0 }}>
            <PieChart
              width={184}
              height={184}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              hideLegend
              series={[
                {
                  data: slices.map((s) => ({
                    id: s.id,
                    value: s.value,
                    label: s.label,
                    color: s.color,
                  })),
                  innerRadius: 58,
                  outerRadius: 90,
                  paddingAngle: 1.5,
                  cornerRadius: 4,
                  valueFormatter: (v) => `${money(v.value, format)} · ${pct(v.value)}`,
                },
              ]}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                pointerEvents: 'none',
                textAlign: 'center',
              }}
            >
              <Box>
                <Typography variant="h6" className="tabular" sx={{ lineHeight: 1.1 }}>
                  {moneyCompact(total, format)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  received
                </Typography>
              </Box>
            </Box>
          </Box>
          <Stack
            component="ul"
            aria-label="Payment methods"
            spacing={1}
            sx={{ flex: 1, minWidth: 0, width: '100%', m: 0, p: 0 }}
          >
            {slices.map((s) => (
              <Stack
                key={s.id}
                component="li"
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', listStyle: 'none' }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '3px',
                    bgcolor: s.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {s.label}
                </Typography>
                <Typography variant="body2" className="tabular" sx={{ fontWeight: 600 }}>
                  {moneyCompact(s.value, format)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  className="tabular"
                  sx={{ width: 36, textAlign: 'right' }}
                >
                  {pct(s.value)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Panel>
  );
}

/** The five customers who bought the most: ranked horizontal bars, values labelled. */
export function TopCustomers({ current, rangeLabel }: Range) {
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['reports', 'sales-by-customer', current, 5],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/sales/by-customer', {
          params: { query: { ...current, page: 1, pageSize: 5 } },
        }),
      ),
  });
  const rows = (q.data?.data ?? []).filter((r) => Number(r.total) > 0);
  const max = Math.max(1, ...rows.map((r) => Number(r.total)));
  return (
    <Panel title="Top customers" subtitle={`By invoiced total · last ${rangeLabel}`}>
      {q.isLoading ? (
        <Skeleton variant="rounded" height={220} />
      ) : rows.length === 0 ? (
        <Empty height={220}>Customers with named invoices will be ranked here.</Empty>
      ) : (
        <Stack spacing={1.5} component="ol" sx={{ m: 0, p: 0 }} aria-label="Top customers">
          {rows.map((r) => (
            <Box component="li" key={r.contactId} sx={{ listStyle: 'none' }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 0.5 }}>
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
                  {r.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {plural(r.invoiceCount, 'invoice')}
                </Typography>
                <Typography
                  variant="body2"
                  className="tabular"
                  sx={{ fontWeight: 600 }}
                  title={money(r.total, format)}
                >
                  {moneyCompact(r.total, format)}
                </Typography>
              </Stack>
              <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${String((Number(r.total) / max) * 100)}%`,
                    borderRadius: 4,
                    bgcolor: 'primary.main',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Panel>
  );
}

/** Gross margin % per period: one line on a percent axis. */
export function MarginTrend({ current, interval, rangeLabel }: Range) {
  const { format } = useShop();
  const q = useQuery({
    queryKey: ['reports', 'gross-profit', current, interval],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/gross-profit/by-period', {
          params: { query: { ...current, interval } },
        }),
      ),
  });
  const rows = q.data?.data ?? [];
  const points = rows.map((r) =>
    Number(r.netRevenue) > 0 && r.marginPercent !== null ? Number(r.marginPercent) : null,
  );
  const overall = q.data?.totals.marginPercent;
  return (
    <Panel
      title="Gross margin"
      subtitle={
        overall != null && Number(q.data?.totals.netRevenue) > 0
          ? `${String(Math.round(Number(overall)))}% overall · last ${rangeLabel}`
          : `Profit on each sale after cost of goods · last ${rangeLabel}`
      }
    >
      {q.isLoading ? (
        <Skeleton variant="rounded" height={220} />
      ) : points.every((p) => p === null) ? (
        <Empty height={220}>The margin draws here once you have sales with costs.</Empty>
      ) : (
        <Box sx={{ mx: -1 }} data-testid="margin-trend">
          <LineChart
            height={220}
            margin={{ left: 0, right: 28, top: 12, bottom: 0 }}
            grid={{ horizontal: true }}
            hideLegend
            xAxis={[
              {
                scaleType: 'point',
                data: rows.map((r) => r.periodStart.slice(0, 10)),
                valueFormatter: (v: string) => axisDate(v, format.locale),
                tickInterval: (_v: string, i: number) =>
                  rows.length <= 8 || (rows.length - 1 - i) % 7 === 0,
                tickLabelStyle: axisText,
                disableLine: true,
                disableTicks: true,
              },
            ]}
            yAxis={[
              {
                width: 40,
                valueFormatter: (v: number) => `${String(Math.round(v))}%`,
                tickLabelStyle: axisText,
                disableLine: true,
                disableTicks: true,
              },
            ]}
            series={[
              {
                id: 'margin',
                label: 'Gross margin',
                data: points,
                connectNulls: true,
                showMark: rows.length <= 14,
                curve: 'monotoneX',
                color: 'var(--viz-3)',
                valueFormatter: (v: number | null) =>
                  v === null ? 'No sales' : `${v.toFixed(1)}%`,
              },
            ]}
            sx={{ ...chartSx, '& .MuiLineElement-root': { strokeWidth: 2 } }}
          />
        </Box>
      )}
    </Panel>
  );
}

const LEVEL_MIX = [0, 22, 45, 70, 100];

/** Calendar heatmap of daily sales over the last 12 weeks, with the busiest weekday called out. */
export function SalesHeatmap({ today }: { today: string }) {
  const { format } = useShop();
  const from = addDays(today, -(HEATMAP_WEEKS * 7 + 6));
  const range = { from, to: today };
  const q = useQuery({
    queryKey: ['reports', 'sales-by-period', range, 'day'],
    queryFn: () =>
      unwrapJson(
        api.GET('/reports/sales/by-period', { params: { query: { ...range, interval: 'day' } } }),
      ),
  });
  const map = salesHeatmap(q.data?.data ?? [], today);
  const best = map.weekdayAverage.reduce((b, v, i, a) => (v > (a[b] ?? 0) ? i : b), 0);
  const hasSales = map.weekdayAverage.some((v) => v > 0);
  const CELL = 'minmax(14px, 1fr)';
  return (
    <Panel
      title="Busiest days"
      subtitle={
        hasSales
          ? `${WEEKDAY_NAMES[best] ?? ''} sells most: ${money(map.weekdayAverage[best] ?? 0, format)} on average · last 12 weeks`
          : 'Daily sales over the last 12 weeks'
      }
    >
      {q.isLoading ? (
        <Skeleton variant="rounded" height={190} />
      ) : (
        <Box data-testid="sales-heatmap">
          <Box
            role="grid"
            aria-label="Daily sales, last 12 weeks"
            sx={{
              display: 'grid',
              gridTemplateColumns: `32px repeat(${String(HEATMAP_WEEKS)}, ${CELL})`,
              gap: '3px',
              alignItems: 'center',
            }}
          >
            {WEEKDAYS.map((day, d) => (
              <Box key={day} role="row" sx={{ display: 'contents' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  role="rowheader"
                  sx={{ fontSize: 11 }}
                >
                  {d % 2 === 0 ? day : ''}
                </Typography>
                {map.weeks.map((week) => {
                  const c = week[d];
                  if (!c) return null;
                  const label = c.future
                    ? axisDate(c.date, format.locale)
                    : `${axisDate(c.date, format.locale)}: ${c.total ? `${money(c.total, format)} · ${plural(c.invoices, 'invoice')}` : 'no sales'}`;
                  return (
                    <Tooltip key={c.date} title={label} disableInteractive>
                      <Box
                        role="gridcell"
                        aria-label={label}
                        sx={(t) => ({
                          aspectRatio: '1',
                          borderRadius: '4px',
                          bgcolor: c.future
                            ? 'transparent'
                            : c.level === 0
                              ? (t.vars ?? t).palette.action.hover
                              : `color-mix(in srgb, ${(t.vars ?? t).palette.primary.main} ${String(LEVEL_MIX[c.level])}%, ${(t.vars ?? t).palette.background.paper})`,
                          border: c.future ? '1px dashed' : 'none',
                          borderColor: 'divider',
                          '&:hover': { outline: '2px solid', outlineColor: 'text.primary' },
                        })}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            ))}
          </Box>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ alignItems: 'center', justifyContent: 'flex-end', mt: 1.5 }}
          >
            <Typography variant="caption" color="text.secondary">
              Less
            </Typography>
            {LEVEL_MIX.map((m, i) => (
              <Box
                key={m}
                aria-hidden
                sx={(t) => ({
                  width: 12,
                  height: 12,
                  borderRadius: '3px',
                  bgcolor:
                    i === 0
                      ? (t.vars ?? t).palette.action.hover
                      : `color-mix(in srgb, ${(t.vars ?? t).palette.primary.main} ${String(m)}%, ${(t.vars ?? t).palette.background.paper})`,
                })}
              />
            ))}
            <Typography variant="caption" color="text.secondary">
              More
            </Typography>
          </Stack>
        </Box>
      )}
    </Panel>
  );
}
